import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Card, Stack, Group, Text, Button, Textarea, Badge,
  ThemeIcon, Progress, Divider, Alert, Collapse, Table
} from '@mantine/core';
import {
  IconCheck, IconPencil, IconRotateClockwise,
  IconSparkles, IconBrain, IconChevronDown, IconChevronUp,
  IconAlertTriangle, IconCompass, IconArrowRight, IconInfoCircle
} from '@tabler/icons-react';
import AIGeneratingIcon from '../../components/AIGeneratingIcon';
import { selectInheritedBuilderRisks } from '../../redux/slices/risksSlice';
import { authFetch } from '../../utils/authFetch';

// Priority color mapping (consistent with app standard)
const PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: '#fff' },
  High: { bg: '#993333', text: '#fff' },
  Significant: { bg: '#B94D4D', text: '#fff' },
  Moderate: { bg: '#D4AA7D', text: '#333' },
  Low: { bg: '#B8C2B7', text: '#333' },
  Negligible: { bg: '#D8D8D8', text: '#333' }
};

// Explorer organizational risk areas (constant - defined outside component to avoid re-creation)
const EXPLORER_AREAS = [
  'commercialRisk', 'operationalRisk', 'complianceRisk',
  'societalRisk', 'environmentalRisk', 'reputationalRisk'
];

/**
 * ExplorerNarrativeCard - Narrative card for Explorer assessments
 * Shows inherited risks from Builder + generates organizational narrative
 */
export default function ExplorerNarrativeCard({
  modelId,
  assessmentUuid,
  parentAssessmentUuid,
  bucket,
  path,
  businessContext: initialBusinessContext,
  useCase: initialUseCase,
  riskFormData,
  onApproved
}) {
  // Step: context | generating | ready | approved | failed
  const [status, setStatus] = useState('context'); // Start at context forms, not loading
  const [narrative, setNarrative] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [inheritedRisksSummary, setInheritedRisksSummary] = useState('');
  const [showInheritedRisks, setShowInheritedRisks] = useState(true);
  const [showAllRisks, setShowAllRisks] = useState(false); // Toggle to show all vs limited
  const [focusAreas, setFocusAreas] = useState([]);

  // Get inherited model risks from Redux (independent of narrative generation)
  const reduxInheritedRisks = useSelector(state =>
    parentAssessmentUuid ? selectInheritedBuilderRisks(state, parentAssessmentUuid) : []
  );

  // Get inherited security scenarios from Redux
  // Note: Some scenario properties may be objects - convert to strings safely
  const inheritedSecurityScenarios = useSelector(state => {
    if (!parentAssessmentUuid) return [];
    const scenarios = state.security?.byAssessment?.[parentAssessmentUuid]?.scenarios || {};
    return Object.values(scenarios).map(scenario => {
      // Helper to safely convert value to string (handles objects)
      const toString = (val, fallback = '') => {
        if (val === null || val === undefined) return fallback;
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
          return Object.keys(val).join(', ') || fallback;
        }
        return String(val);
      };

      return {
        riskId: scenario.scenarioId || scenario.id || `scenario_${Math.random().toString(36).slice(2)}`,
        riskName: toString(scenario.name, toString(scenario.scenarioName, 'Unnamed Scenario')),
        priority: toString(scenario.impact, toString(scenario.priority, 'Moderate')),
        area: 'Security Assessment',
        category: toString(scenario.attackSurface, 'Security'),
        type: 'SecurityScenario'
      };
    });
  });

  // Combine model risks + security scenarios
  const inheritedRisks = useMemo(() => {
    const modelRisks = reduxInheritedRisks.map(risk => ({
      ...risk,
      riskName: risk.riskName || risk.scenarioText || 'Unnamed Risk',
      area: risk.sectionName || risk.section || 'Unknown',
      priority: risk.priority || null
    }));
    return [...modelRisks, ...inheritedSecurityScenarios];
  }, [reduxInheritedRisks, inheritedSecurityScenarios]);

  // Determine which risks to display based on showAllRisks toggle
  const INITIAL_DISPLAY_COUNT = 10;
  const displayedRisks = showAllRisks ? inheritedRisks : inheritedRisks.slice(0, INITIAL_DISPLAY_COUNT);

  // Local context form state (editable before generation)
  const [localBusinessContext, setLocalBusinessContext] = useState(initialBusinessContext || '');
  const [localUseCase, setLocalUseCase] = useState(initialUseCase || '');

  // Generate Explorer narrative with inherited context
  const generateNarrative = useCallback(async () => {
    try {
      setStatus('generating');
      setKeywords([]);
      setNarrative('');

      console.log('[ExplorerNarrativeCard] Generating Explorer narrative with payload:', {
        modelId,
        assessmentUuid,
        parentAssessmentUuid,
        bucket,
        path,
        hasBusinessContext: !!localBusinessContext,
        hasUseCase: !!localUseCase
      });

      const res = await authFetch('/.netlify/functions/explorerGenerateNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          parentAssessmentUuid,
          bucket,
          path,
          businessContext: localBusinessContext,
          useCase: localUseCase
        })
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Generation failed');
        throw new Error(errorText || 'Request failed');
      }

      const data = await res.json();
      if (data.success && data.narrative) {
        setNarrative(data.narrative);
        setInheritedRisksSummary(data.inheritedRisksSummary || '');
        setFocusAreas(data.focusAreas || []);

        // Note: inherited risks now come from Redux selector, not API response

        // Generate keywords from Explorer areas and focus areas
        const displayKeywords = [
          ...EXPLORER_AREAS.map(a => a.replace('Risk', '')),
          ...(data.focusAreas || []).slice(0, 5)
        ].slice(0, 12);
        setKeywords(displayKeywords);
        setStatus('ready');
      } else {
        throw new Error(data.error || 'Narrative generation failed');
      }
    } catch (error) {
      console.error('[ExplorerNarrativeCard] Generation error:', error);
      setStatus('failed');
      setNarrative('');
    }
  }, [modelId, assessmentUuid, parentAssessmentUuid, bucket, path, localBusinessContext, localUseCase]);

  // Handler for submitting context form and starting generation
  const handleSubmitContext = useCallback(() => {
    if (!modelId || !assessmentUuid) {
      console.error('[ExplorerNarrativeCard] Missing modelId or assessmentUuid');
      return;
    }
    generateNarrative();
  }, [modelId, assessmentUuid, generateNarrative]);

  const handleApprove = () => {
    setStatus('approved');
    onApproved?.(narrative, focusAreas);
  };

  const handleRegenerate = () => {
    setStatus('context'); // Go back to context form to allow editing
    setNarrative('');
  };

  const statusBadge = (() => {
    switch (status) {
      case 'context':
        return <Badge variant="light" color="blue">Enter Context</Badge>;
      case 'generating':
        return (
          <Badge variant="light" color="blue">
            <Group gap="xs">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
              <Text size="sm">Generating...</Text>
            </Group>
          </Badge>
        );
      case 'ready':
        return <Badge variant="light" color="grape">Ready for Review</Badge>;
      case 'approved':
        return (
          <Badge variant="filled" color="grape">
            <Group gap="xs">
              <IconCheck size={14} />
              <Text size="sm">Approved</Text>
            </Group>
          </Badge>
        );
      case 'failed':
        return <Badge variant="light" color="red">Generation Failed</Badge>;
      default:
        return <Badge variant="outline" color="gray">Loading...</Badge>;
    }
  })();

  const borderColor = ({
    context: '#3b82f6',
    generating: '#9C36B5',
    ready: '#9C36B5',
    approved: '#862E9C',
    failed: '#dc2626',
    loading: '#6b7280',
  })[status] || '#6b7280';

  const canEdit = status === 'ready' || status === 'approved';
  const canApprove = status === 'ready' || status === 'approved';
  const progress = status === 'approved' ? 100 : (status === 'ready' ? 100 : (status === 'generating' ? 50 : (status === 'context' ? 10 : 25)));

  return (
    <Stack gap="lg">
      {/* Header with Progress */}
      <Card withBorder p="lg" style={{ backgroundColor: 'rgba(174, 62, 201, 0.05)' }}>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'grape', to: 'violet' }}>
                  <IconCompass size={24} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={600} size="lg">Explorer Risk Analysis</Text>
                  <Text size="sm" c="dimmed">
                    Assessing business and risk impacts
                  </Text>
                </Stack>
              </Group>
            </div>
            {statusBadge}
          </Group>
          <Progress value={progress} size="sm" color={status === 'context' ? 'blue' : 'grape'} />
        </Stack>
      </Card>

      {/* Context Form - Shown before narrative generation */}
      {status === 'context' && (
        <Card withBorder p="lg" style={{ borderColor: '#3b82f6' }}>
          <Stack gap="md">
            <Group gap="md">
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconPencil size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={600}>Business Context & Use Case</Text>
                <Text size="sm" c="dimmed">
                  Provide context to guide the Explorer risk analysis
                </Text>
              </Stack>
            </Group>

            <Divider />

            <Stack gap="md">
              <Textarea
                label="Business Context"
                description="Describe the business environment, stakeholders, and organizational context"
                placeholder="e.g., Our organization is a mid-sized financial services company. Key stakeholders include regulatory compliance, risk management, and customer service departments..."
                value={localBusinessContext}
                onChange={(e) => setLocalBusinessContext(e.target.value)}
                minRows={4}
                autosize
              />

              <Textarea
                label="Use Case Description"
                description="Describe how the AI system will be used in your organization"
                placeholder="e.g., The AI system will be used to automate customer support inquiries, route complex cases to human agents, and provide real-time analytics to management..."
                value={localUseCase}
                onChange={(e) => setLocalUseCase(e.target.value)}
                minRows={4}
                autosize
              />
            </Stack>

            <Group justify="flex-end" mt="md">
              <Button
                color="blue"
                rightSection={<IconArrowRight size={16} />}
                onClick={handleSubmitContext}
              >
                Generate Narrative
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Inherited Risks Panel - ALWAYS shown (independent of narrative generation) */}
      <Card withBorder p="md" style={{ borderColor: parentAssessmentUuid ? '#AE3EC9' : '#868e96' }}>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color={parentAssessmentUuid ? 'grape' : 'gray'}>
                <IconBrain size={14} />
              </ThemeIcon>
              <Text fw={500} size="sm">Technical Risks</Text>
              <Badge size="sm" variant="light" color={parentAssessmentUuid ? 'grape' : 'gray'}>
                {inheritedRisks.length} risks
              </Badge>
            </Group>
            {parentAssessmentUuid && inheritedRisks.length > 0 && (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setShowInheritedRisks(!showInheritedRisks)}
                rightSection={showInheritedRisks ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              >
                {showInheritedRisks ? 'Hide' : 'Show'}
              </Button>
            )}
          </Group>

          {!parentAssessmentUuid ? (
            // Standalone Explorer - no linked Builder
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">
                This is a standalone Explorer assessment. No Builder assessment was linked during creation.
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                To inherit technical risks, create a new Explorer assessment and select a completed Builder assessment.
              </Text>
            </Alert>
          ) : inheritedRisks.length === 0 ? (
            // Linked Builder but no risks loaded
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                No risks found in the linked Builder assessment.
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                The Builder assessment may not have saved risks yet, or the risks may still be loading.
              </Text>
            </Alert>
          ) : (
            // Show risks table
            <Collapse in={showInheritedRisks}>
              {inheritedRisksSummary && (
                <Text size="sm" c="dimmed" mb="sm">{inheritedRisksSummary}</Text>
              )}
              <Table striped highlightOnHover withTableBorder fontSize="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Risk</Table.Th>
                    <Table.Th>Area</Table.Th>
                    <Table.Th>Priority</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {displayedRisks.map((risk, idx) => {
                    const priorityConfig = PRIORITY_COLORS[risk.priority] || { bg: '#e9ecef', text: '#495057' };
                    return (
                      <Table.Tr key={idx}>
                        <Table.Td>{risk.riskName}</Table.Td>
                        <Table.Td>
                          <Badge
                            size="xs"
                            variant="light"
                            color={risk.area === 'Security Assessment' ? 'red' : 'gray'}
                          >
                            {risk.area}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="xs"
                            style={{
                              backgroundColor: priorityConfig.bg,
                              color: priorityConfig.text
                            }}
                          >
                            {risk.priority || 'Not Evaluated'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              {inheritedRisks.length > INITIAL_DISPLAY_COUNT && (
                <Group justify="space-between" mt="xs">
                  <Text size="xs" c="dimmed">
                    Showing {displayedRisks.length} of {inheritedRisks.length} risks
                  </Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => setShowAllRisks(!showAllRisks)}
                  >
                    {showAllRisks ? 'Show Less' : `Show All ${inheritedRisks.length} Risks`}
                  </Button>
                </Group>
              )}
            </Collapse>
          )}
        </Stack>
      </Card>

      {/* Focus Areas - only shown after context is submitted */}
      {status !== 'context' && focusAreas.length > 0 && (
        <Card withBorder p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>Suggested Focus Areas</Text>
            <Group gap="xs">
              {focusAreas.map((area, idx) => (
                <Badge key={idx} variant="light" color="grape" size="sm">
                  {area.replace(/([A-Z])/g, ' $1').replace('Risk', '').trim()}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Card>
      )}

      {/* Narrative Content - only shown after context is submitted */}
      {status !== 'context' && (
      <Card withBorder p="lg" style={{ borderColor }}>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="grape">
                <IconSparkles size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Risk Narrative</Text>
            </Group>
            {canEdit && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel Edit' : 'Edit'}
              </Button>
            )}
          </Group>

          {status === 'generating' && (
            <Stack align="center" py="xl">
              <AIGeneratingIcon isGenerating size={48} />
              <Text size="sm" c="dimmed">Analyzing context...</Text>
            </Stack>
          )}

          {status === 'failed' && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Stack gap="xs">
                <Text size="sm">Failed to generate narrative. Please try again.</Text>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  onClick={handleRegenerate}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          )}

          {(status === 'ready' || status === 'approved') && (
            <>
              {isEditing ? (
                <Textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  minRows={10}
                  autosize
                />
              ) : (
                <Text
                  size="sm"
                  style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {narrative}
                </Text>
              )}

              {/* Keywords */}
              {keywords.length > 0 && (
                <>
                  <Divider />
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Keywords:</Text>
                    {keywords.map((kw, idx) => (
                      <Badge key={idx} size="xs" variant="outline" color="grape">
                        {kw}
                      </Badge>
                    ))}
                  </Group>
                </>
              )}

              {/* Actions */}
              <Group justify="space-between" mt="md">
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconRotateClockwise size={16} />}
                  onClick={handleRegenerate}
                  disabled={status === 'approved'}
                >
                  Regenerate
                </Button>
                {canApprove && status !== 'approved' && (
                  <Button
                    color="grape"
                    size="sm"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApprove}
                  >
                    Approve & Continue
                  </Button>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Card>
      )}
    </Stack>
  );
}
